import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import Cookie from 'react-cookies';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from "react-i18next";

import Settings from '../../data/settings';
import { ILayoutContext } from '../';

export interface CookiePreferences {
  [essential: string]: boolean;
  functionality: boolean;
  performance: boolean;
  targeting: boolean;
}

enum CookieTypes {
  essential,
  functionality,
  performance,
  targeting
}

interface CookieType {
  type: CookieTypes;
  title: string;
  description: string;
}

interface CookieTypeCardProps {
  disabled?: boolean;
  expanded: boolean;
  checked: boolean;
  cookieType: CookieType;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
}

const cookieName = "cookie-preferences";

const CookiesManager = (props: { onAccepted: (p: CookiePreferences) => void }) => {
  const { t } = useTranslation();

  const [isManaging, setIsManaging] = useState<boolean>(false);

  // These settings are stored when the user accepts all cookies
  const defaultPreferences = {
    essential: true,
    functionality: true,
    performance: true,
    targeting: true
  };

  const [cookiePreferences, setCookiePreferences] = React.useState<CookiePreferences>({
    essential: true,
    functionality: true,
    performance: false,
    targeting: false
  });

  const [expanded, setExpanded] = React.useState<CookiePreferences>({
    essential: false,
    functionality: false,
    performance: false,
    targeting: false
  });

  const onAccepted = (all = false) => {
    const p = all ? defaultPreferences : cookiePreferences;

    Cookie.save(cookieName, p, { path: '/', maxAge: 1000 * 24 * 3600 });

    props.onAccepted(p);
  }

  const handleExpandCookieType = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    const e: CookiePreferences = { ...expanded };
    e[panel] = isExpanded;

    setExpanded(e);
  };

  const CookieTypeCard = ({ disabled, expanded, cookieType, checked, onChange }: CookieTypeCardProps) => {
    return (
      <Accordion className="cookie-collapsible-card" expanded={expanded} onChange={handleExpandCookieType(CookieTypes[cookieType.type])}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ width: '20%', flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
            <Switch inputProps={{ 'aria-label': 'Switch demo' }}
              disabled={disabled}
              onClick={(event) => event.stopPropagation()}
              onChange={onChange} checked={checked} />
          </Typography>

          <Typography sx={{ color: 'text.secondary' }}>{cookieType.title}</Typography>
        </AccordionSummary>

        <AccordionDetails>
          <p className="cookie-description">{cookieType.description}</p>
        </AccordionDetails>
      </Accordion>);
  }

  return (<>
    <h1>{t('before-you-continue', { projectTitle: Settings.project.title })}</h1>
    <p>{t('intro')}</p>

    <div className="footer">
      {!isManaging && <Button variant="outlined" onClick={() => setIsManaging(true)}>{t('manage-preferences')}</Button>}
      <Button onClick={() => onAccepted(true)} variant="contained">{t('accept-all')}</Button>
    </div>

    {isManaging && <>
      <h2>{t('manage-cookies')}</h2>

      <div className="more-info">
        {t('more-info', { websiteTitle: "Uderly" })}
      </div>

      <div className="cookie-settings">
        <CookieTypeCard expanded={expanded.essential === true} disabled
          checked={cookiePreferences.essential === true}
          cookieType={{ type: CookieTypes.essential, title: t('essential-cookies'), description: t('essential-cookies-desc') }} />

        <CookieTypeCard expanded={expanded.functionality}
          onChange={(_event, checked) => setCookiePreferences({ ...cookiePreferences, functionality: checked })}
          checked={cookiePreferences.functionality}
          cookieType={{ type: CookieTypes.functionality, title: t('functionality-cookies'), description: t('functionality-cookies-desc') }} />

        <CookieTypeCard expanded={expanded.performance}
          onChange={(_event, checked) => setCookiePreferences({ ...cookiePreferences, performance: checked })}
          checked={cookiePreferences.performance}
          cookieType={{ type: CookieTypes.performance, title: t('performance-cookies'), description: t('performance-cookies-desc') }} />

        <CookieTypeCard expanded={expanded.targeting}
          onChange={(_event, checked) => setCookiePreferences({ ...cookiePreferences, targeting: checked })}
          checked={cookiePreferences.targeting}
          cookieType={{ type: CookieTypes.targeting, title: t('targeting-cookies'), description: t('targeting-cookies-desc') }} />

        <Button onClick={() => onAccepted(false)} variant="contained">{t('save-close')}</Button>
      </div>
    </>}
  </>);
}

const useCookiesManager = (layoutContext: ILayoutContext,
  onAccepted?: (p?: CookiePreferences) => void, onLoaded?: (p?: CookiePreferences) => void) => {
  const { t } = useTranslation();

  useEffect(() => {
    const acceptedPreferences = Cookie.load(cookieName);

    if (onLoaded)
      onLoaded(acceptedPreferences);

    const logo = <>
      {Settings.provider?.web &&
        <a href={"https://" + Settings.provider.web.site} target='_blank' rel="noreferrer">
          <img className="uderly-logo" src="https://api.net.uderly.com/mediastock/1/media/file?name=uderly-logo-color-dark.png" alt="Uderly" />
        </a>}

      {t('website-from', { providerTitle: Settings.provider?.title })}
    </>;

    const check = async () => {
      layoutContext.dialog.show({
        title: logo,
        content: <CookiesManager onAccepted={(p) => {
          if (onAccepted)
            onAccepted(p);

          layoutContext.dialog.hide();
        }} />,
        modal: true,
        className: "cookies-manager"
      });
    }

    if (!layoutContext.dialog.isOpen() && (!acceptedPreferences || !acceptedPreferences.essential))
      check();
  }, [layoutContext.dialog, t]);
}

export default useCookiesManager;